import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { convertGraphqlBaseTestItemToDto, convertGraphqlFormFieldToDto, convertGraphqlResponseToDto, convertGraphqlResponseToDtoSimpleReturnValue } from 'src/app/graphQL/mappers/common.mappers';
import { convertEeseCoverageDtoToGraphqlInput } from 'src/app/graphQL/mappers/eese-coverage.mappers';
import { GraphQLBaseTestItemOutput } from 'src/app/graphQL/models/output/graphql-base-test-item-output';
import { GraphQLFormFieldOutput } from 'src/app/graphQL/models/output/graphql-form-field-output';
import { GraphQLResponseOutput } from 'src/app/graphQL/models/output/graphql-response-output';
import { CREATE_EESE_COVERAGE_MUTATION, DELETE_EESE_COVERAGE_MUTATION, UPDATE_EESE_COVERAGE_MUTATION } from 'src/app/graphQL/mutations/eese-coverage-mutations';
import { GETALL_EESE_COVERAGE_QUERY, GET_EESE_COVERAGE_QUERY, GET_EESE_COVERAGE_SEARCH_UI_TEMPLATE_QUERY, GET_EESE_COVERAGE_UI_TEMPLATE_QUERY, SEARCH_EESE_COVERAGE_QUERY } from 'src/app/graphQL/queries/eese-coverage-queries';
import { BaseTestItemDto } from 'src/app/models/dto/Base-Test-item-dto';
import { EeseCoverageDto } from 'src/app/models/dto/eeseCoverageDto';
import { ResponseDto } from 'src/app/models/dto/responseDto';
import { formField } from 'src/app/models/formField';
import { ApolloClientService } from './apollo-client.service';
import { DualApiService, Scope } from '../migration/dual-api.service';

@Injectable({
  providedIn: 'root',
})
export class EeseCoverageApiService {

  constructor(
    private apolloClientService: ApolloClientService,
    private dualApiService: DualApiService
  ) {}

  /**
   * `regBy` is the old API's "only mine" switch, which maps onto the Go lambda's
   * `scope: MINE` (resolved server-side from the authorizer, so the client cannot ask for
   * someone else's items). Nothing maps to MY_GROUP: no EESE coverage item carries
   * functionalGroupOwners, so that scope would always return an empty page.
   */
  getAll(regBy: string): Observable<BaseTestItemDto[]> {
    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{configuratorAllEeseCoverages : GraphQLBaseTestItemOutput[]}>({
          query: GETALL_EESE_COVERAGE_QUERY,
          variables: {RegBy: regBy}
        })
      ),
      map(results => {
        const eeseCoverages = results.data.configuratorAllEeseCoverages
        if (!eeseCoverages)
          return undefined

        return eeseCoverages.map(item => {
          return convertGraphqlBaseTestItemToDto(item);
        })
      })
    );

    const scope: Scope | undefined = regBy ? 'MINE' : undefined;

    return this.dualApiService.dualGetAll('eeseCoverage', [], oldApiCall$, scope);
  }

  getByUid(uid: string): Observable<ResponseDto> {
    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{configuratorEeseCoverageById: GraphQLResponseOutput}>({
          query: GET_EESE_COVERAGE_QUERY,
          variables: {Uid: uid}
        })
      ),
      map(results => {
        const eeseCoverage = results.data.configuratorEeseCoverageById
        if (!eeseCoverage)
          return undefined

        return convertGraphqlResponseToDto<EeseCoverageDto>(eeseCoverage);
      })
    );

    return this.dualApiService.dualGetByUid('eeseCoverage', uid, oldApiCall$);
  }

  getUiTemplate(): Observable<formField[]> {
    return this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{configuratorEeseCoverageUiTemplate: GraphQLFormFieldOutput[]}>({
          query: GET_EESE_COVERAGE_UI_TEMPLATE_QUERY,
          variables: {}
        })
      ),
      map(results => {
        const eeseCoverageUiTemplate = results.data.configuratorEeseCoverageUiTemplate
        if (!eeseCoverageUiTemplate)
          return []

        return eeseCoverageUiTemplate.map(item => {
          return convertGraphqlFormFieldToDto(item);
        })
      })
    )
  }

  create(eeseCoverage: any): Observable<ResponseDto> {
    const graphqlEeseCoverageInput = convertEeseCoverageDtoToGraphqlInput(eeseCoverage)

    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.mutate<{configuratorCreateEeseCoverage: GraphQLResponseOutput}>({
          mutation: CREATE_EESE_COVERAGE_MUTATION,
          variables: {EeseCoverage: graphqlEeseCoverageInput }
        })
      ),
      map(result => {
        const createdEeseCoverage = result.data.configuratorCreateEeseCoverage
        if (!createdEeseCoverage)
          return undefined

        return convertGraphqlResponseToDtoSimpleReturnValue(createdEeseCoverage as GraphQLResponseOutput)
      })
    );

    // The DTO, not the PascalCase GraphQL input: GoLambdaMapperService does its own
    // camelCase conversion and Go-schema cleaning.
    return this.dualApiService.dualCreate('eeseCoverage', eeseCoverage, oldApiCall$);
  }

  update(eeseCoverage: any): Observable<ResponseDto> {
    const graphqlEeseCoverageInput = convertEeseCoverageDtoToGraphqlInput(eeseCoverage)

    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.mutate<{configuratorUpdateEeseCoverage: GraphQLResponseOutput}>({
          mutation: UPDATE_EESE_COVERAGE_MUTATION,
          variables: {EeseCoverage: graphqlEeseCoverageInput }
        })
      ),
      map(result =>{
        const updatedEeseCoverage = result.data.configuratorUpdateEeseCoverage
        if (!updatedEeseCoverage)
          return undefined

        return convertGraphqlResponseToDtoSimpleReturnValue(result.data.configuratorUpdateEeseCoverage as GraphQLResponseOutput)
      } )
    );

    // dualUpdate strips uid out of the payload — the Go mutation takes it as its own
    // argument and rejects it inside data.
    return this.dualApiService.dualUpdate('eeseCoverage', eeseCoverage.uid, eeseCoverage, oldApiCall$);
  }

  /** `regBy` is unused by the Go lambda — it identifies the caller from the authorizer. */
  delete(uid: string, regBy: string): Observable<any> {
    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.mutate({
          mutation: DELETE_EESE_COVERAGE_MUTATION,
          variables: {Uid: uid}
        })
      ),
      map(result => result)
    );

    return this.dualApiService.dualDelete('eeseCoverage', uid, oldApiCall$);
  }

  search(eeseCoverage: EeseCoverageDto, scope?: Scope): Observable<BaseTestItemDto[]> {
    const graphqlEeseCoverageInput = convertEeseCoverageDtoToGraphqlInput(eeseCoverage)

    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{configuratorSearchEeseCoverage: GraphQLBaseTestItemOutput[]}>({
          query: SEARCH_EESE_COVERAGE_QUERY,
          variables: {EeseCoverage: graphqlEeseCoverageInput}
        })
      ),
      map(results => {
        const eeseCoverages = results.data.configuratorSearchEeseCoverage
        if (!eeseCoverages)
          return undefined

        return eeseCoverages.map(item => {
          return convertGraphqlBaseTestItemToDto(item);
        })
      })
    );

    return this.dualApiService.dualSearch('eeseCoverage', eeseCoverage, oldApiCall$, scope);
  }

  getSearchUiTemplate(): Observable<formField[]> {
    return this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{configuratorEeseCoverageSearchUiTemplate: GraphQLFormFieldOutput[]}>({
          query: GET_EESE_COVERAGE_SEARCH_UI_TEMPLATE_QUERY,
          variables: {}
        })
      ),
      map(results => {
        const searchUiTemplate = results.data.configuratorEeseCoverageSearchUiTemplate
        if (!searchUiTemplate)
          return undefined

        return searchUiTemplate.map(item => {
          return convertGraphqlFormFieldToDto(item);
        })
      })
    )
  }
}
